import { GlobalConstants } from "../../constants/GlobalConstants";
import LmsTable, { ITableActionCustomButton, LMSTableProps, TableActionButtonTypes } from "./LmsTable";
import  { FC } from "react";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { IdProps } from "../../interfaces/Global";
import { appendParams, useFetchData, appendParam } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "./LmsFormGenerator";
import { loanConditionFields } from "../Servicing/LmsServicingLoanConditions";
import LmsTabPages from "./LmsTabPages";
import { TABLES } from "../../constants/GlobalLabelsDictionaries";
import { guid } from "../../../Helpers";

interface LmsLoanSharedTableProps {
  suffix?: string
  source?: string
  title?: string
  loanDetailsId?: string
  overrideDefaultTableProps?: LMSTableProps;
  useApiFiltering?: boolean
}

interface ITableProps extends LmsLoanSharedTableProps{
  suffix?: string;
  path?: string;
  editPath?: string;
  skipClick?: boolean;
  id?: string;
  orgId?: string | null;
  loanId?: string;
  borrowerOrganizationId?: string;
  isSpe?: boolean;
}

interface LmsLoanSharedTableByLoanIdProps extends LmsLoanSharedTableProps {
  loanId?: string;
  loanDetailsId?: string
  title?: string;
  hideTitle?: boolean;
  suffix?: string;
  isServicing?: boolean;
  useApiFiltering?: boolean;
}

interface IConditionsTableProps extends LmsLoanSharedTableProps {
  loanId?: string;
}

export const CommentsTable = ({ suffix = "", path = "organizations", orgId = "", borrowerOrganizationId = "" }: ITableProps) => {
  const actionsButtons = [
    { text: "Add", path: appendParam(`/${path}/addcomment?orgId=${orgId}`, "borrowerOrganizationId", borrowerOrganizationId) },
    "export"
  ];

  if (!borrowerOrganizationId) {
    actionsButtons.push("archive");
  }

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    archiveSuffix="orgs/archive-comments"
    columnLabelsDictionary={{ organizationCommentCategory: "Category", description: "Comments" }}
    onClickPath={appendParam(`/${path}/viewcomment/{id}`, "borrowerOrganizationId", borrowerOrganizationId)}
  />;
};

export const BanksTable = ({ suffix = "" }: ITableProps) => {
  const actionsButtons = ["export"];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    sortable={["Created On"]}
    useGlobalDictionary
    onClickPath="organizations/viewbank"
  />;
};

export const StateTaxTable = ({ suffix = "", path = "organizations", orgId = "", borrowerOrganizationId }: ITableProps) => {
  const edithPath = appendParam(`/${path}/editstatetax/:id`, "borrowerOrganizationId", borrowerOrganizationId);
  const actionsButtons = [
    { text: "Add", path: appendParam(`/${path}/addstatetax?orgId=${orgId}`, "borrowerOrganizationId", borrowerOrganizationId) },
    { text: "Edit", path: edithPath },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix={LmsEndpoints.Orgs.DELETE.STATE_TAX}
    columnLabelsDictionary={{ organizationStateTaxNumber: "Tax ID" }}
    onClickPath={edithPath}
  />;
};

export const InsuranceTable = ({ suffix = "", path = "organizations", orgId, borrowerOrganizationId }: ITableProps) => {
  const edithPath = appendParam(`/${path}/editinsurance/{id}`, "borrowerOrganizationId", borrowerOrganizationId);
  const actionsButtons = [
    { text: "Add", path: appendParam(`/${path}/addinsurance/?orgId=${orgId}`, "borrowerOrganizationId", borrowerOrganizationId) },
    { text: "Edit", path: edithPath },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix={LmsEndpoints.Orgs.DELETE.INSURANCE}
    onClickPath={edithPath}
    columnLabelsDictionary={{
      policyCoverage: "Coverage Type",
      insuranceBrokerOrganization: "Provider",
      insuranceBrokerOrganizationContact: "Provider Agent",
      expiryDate: "Expiration Date",
      policyNumber: "Policy #",
    }}
    rowsBackgroundCustomColor={(row) => !borrowerOrganizationId ? row.expiryDateColorId : ""}
  />;
};

export const ContactsTable = ({ skipClick = false }: ITableProps) => {
  const actionsButtons = [
    "add",
    "edit",
    "remove",
    "export",
    "activate",
    "deactivate"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix="orgs/contacts"
    deleteSuffix="orgs/contacts"
    activateSuffix="orgs/activate-contacts"
    deActivateSuffix="orgs/deactivate-contacts"
    sortable={["Created On"]}
    skipClick={skipClick}
    columnLabelsDictionary={GlobalConstants.ContactsTableColumnLabelsDictionary}
  />;
};

export const ExistingContactTable = ({ id }) => {
  const actionsButtons = [
    {
      text: "Add Selected",
      icon: GlobalConstants.Icons.ADD,
      action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
      submitSuffix: "orgs/contacts",
      method: "POST",
      data: (checkedIds) => ({ organizationContactIds: checkedIds, organizationId: id }),
      clearSuffixes: [`orgs/contacts/${id}`],
      refreshDataOnSuccess: true
    } as ITableActionCustomButton,
    "close"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={`orgs/${id}/available-contacts-to-assign`}
    clearSuffixesOnSuccess={[`orgs/contact-orgs?orgId=${id}`]}
    columnLabelsDictionary={{
      "mlsnumber": "MLS Number",
      "organizationCsv": "Organization Csv",
      "organizationTypeCsv": "Organization Type Csv"
    }}
  />;
};

export const OrganizationsTable = ({ suffix = "orgs/info-list", path, skipClick = false }: ITableProps) => {
  const addAction = path ? { action: "add", path } : "add";
  const actionsButtons = [
    addAction,
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    skipClick={skipClick}
  />;
};

export const BusinessRolesTable = ({ path = "", suffix = "", skipClick = false }: ITableProps) => {
  const addAction = path ? { action: "add", path } : "add";
  const actionsButtons = [
    addAction,
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/contact-business-roles"
    sortable={["Created On"]}
    skipClick={skipClick}
  />;
};

export const AssetsLiabilitiesTable = ({ suffix, orgId, path = "organizations-borrowers", borrowerOrganizationId = "" }: ITableProps) => {
  const edithPath = appendParam(`${path}/editassestliability/{id}`, "borrowerOrganizationId", borrowerOrganizationId);
  const actionsButtons = [
    { action: "add", path: appendParam(`${path}/addassestliability?orgId=${orgId}`, "borrowerOrganizationId", borrowerOrganizationId) },
    { action: "edit", path: edithPath },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix || `orgs/contact-al-schedule/${orgId}`}
    deleteSuffix="orgs/al-schedules"
    onClickPath={edithPath}
    columnLabelsDictionary={{
      borrowerOrganization: "Borrower",
      alclass: "AL Class",
      altype: "AL Type",
      institution: "Name of Institution",
      value: "Current Balance",
      alstatus: "Status",
      apireference: "API Reference",
    }}
  />;
};

export const AssetsLiabilitiesContactTable = ({ suffix, contactId }) => {
  const editPath = `organizations-contacts/editassestliability/{id}?contactId=${contactId}`;
  const actionsButtons = [
    { action: "add", path: `organizations-contacts/addassestliability?contactId=${contactId}` },
    { action: "edit", path: editPath},
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix || `orgs/contact-al-schedule/${contactId}`}
    deleteSuffix="orgs/al-schedules"
    sortable={["Created On"]}
    onClickPath={editPath}
    columnLabelsDictionary={{
      alclass: "AL Class",
      altype: "AL Type",
      institution: "Name of Institution",
      value: "Current Balance",
      alstatus: "Status",
      apireference: "API Reference",
      borrowerOrganization: "Borrower"
    }}
  />;
};

export const ScheduleOfReoTable = ({ path = "", skipClick = false, orgId, suffix, editPath = "", borrowerOrganizationId, isSpe }: ITableProps) => {
  isSpe = isSpe || !!borrowerOrganizationId;
  if (!editPath) {
    editPath = appendParam(`organizations-contacts/editscheduleofreo/{id}?contactId=${orgId}`, "isSpe", `${isSpe}`);
  }

  const addActionPath = appendParam(`/${path}/addscheduleofreo/${orgId}`, "isSpe", `${isSpe}`);
  const addAction = path ? { action: "add", path: addActionPath } : appendParam("add", "isSpe", `${isSpe}`);
  const actionsButtons = [
    addAction,
    { action: "edit", path: editPath },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/reo-schedules"
    onClickPath={editPath}
    skipClick={skipClick}
    columnLabelsDictionary={{ reotype: "REO Type", reostatus: "REO Status", borrowerOrganization: "Borrower" }}
  />;
};

export const LmsVotingDetailsTable: FC<LmsLoanSharedTableProps> = ({
  suffix,
  overrideDefaultTableProps
}) => {
  const editPath = "loandetails";
  const defaultTableProps: LMSTableProps = {
    apiSuffix: suffix,
    sortable: ["CreatedBy", "CityId", "Borrower"],
    deleteSuffix: "loandetails/conditions",
    onClickPath: editPath,
    actionButtons: [
      { action: "edit", path: editPath },
      "export",
    ]
  };
  return (
    <LmsTable
      {...({
        ...defaultTableProps,
        ...overrideDefaultTableProps,
      })}
      columnLabelsDictionary={{
        additionalCollateralDescription: "Collateral Release Description",
        loanAmountOriginal: "Original Loan Amount",
        phase1Term: "Phase 1 Loan Term",
        description: "Additional Loan Comments",
        estimated1DrawDate: "Estimated 1st Date",
        estimatedConstructionCompletionDate: "Estimated Construction Completion",
        loanNumber: "Loan #",
        borrowerOrganization: "Borrower",
        applicationNumber: "Application #",
        owner: "OWNER",
        rofrvalue: "ROFR Value",
        rofr: "ROFR",
        outOfPocketBc: "Out Of Pocket (BC)",
        loanClosedDate: "Loan Active Date",
        brms: "BRMs",
        initialCos: "Initial COs",
        reviewCos: "Review COs",
        variableRateIndex: "Variable Index Rate",
        trctriggered: "TRC Triggered",
        rateTypeOption: "Rate Type",
        dotrecording: "DOT Recording",
        spreadRate: "Margin",
        rateFloor: "Floor",
        rateCeiling: "Ceiling",
        batierRate: "BA Tier Rate"
      }}
    />
  );
};

export const LmsLoanDetailsTable: FC<LmsLoanSharedTableProps> = ({
  overrideDefaultTableProps
}) => {
  const defaultTableProps: {
    apiSuffix: string;
    sortable: string[];
    onClickPath: string;
    actionButtons: TableActionButtonTypes[];
  } = {
    apiSuffix: "loandetails/list",
    sortable: ["CreatedBy", "CityId", "Borrower"],
    onClickPath: "loandetails",
    actionButtons: [
      { action: "edit", path: "loandetails" },
      "export",
      { text: "Access Report", action: "edit", icon: GlobalConstants.Icons.FILE_OUTLINE, path: "/loandetails/accessreport" }
    ]
  };
  return (
    <LmsTable
      {...({
        ...defaultTableProps,
        ...overrideDefaultTableProps,
      })}
    />
  );
};

export const resendBudgetToInspectionCompanyBtn = {
  method: "PUT",
  action: "submitselected",
  submitSuffix: "/loanproperty/resend-budget-to-inspection-company",
  text: "Resend Budget To Inspection Company",
  isDisabled: (checkedItems, data) => {
    if (checkedItems.length !== 1) {
      return true;
    }

    const selectedRecord = data[checkedItems[0]];
    return !selectedRecord?.readyForNwcc || !selectedRecord?.loanBudgetId || selectedRecord?.nwccprojectId;
  },
} as ITableActionCustomButton;

export const submitValidationIsLoanLost = async (inputs, options) => {
  const { loanId } = inputs;
  const { data } = await options?.fetchData(`/loans/loan-sub-status/${loanId}`);
  if (data.loanSubStatus === "Lost") {
    return {
      errorHeader: "Operation failed",
      errorMessage: "Loan is in lost sub status."
    };
  }
  return { success: true };
};

export const SelectLoanForm = ({ setIsModalOpen }) => {
  const [{ fetchData }] = useFetchData();
  const myForm = new BCFormController({});

  myForm.addCenteredGroup([
    myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", {
      required: true,
      name: "borrowerOrganizationId",
    }),
  ]);

  myForm.addCenteredGroup([
    myForm.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", {
      dependencyField: "borrowerOrganizationId",
    }),
  ]);

  myForm.addCenteredGroup([
    myForm.getDropdownField(
      "Loan",
      "loans/list-borrower-loans",
      {
        required: true,
        dependencyField: "borrowerOrganizationId",
        dependencyFieldQuery: "borrowerOrganizationId",
        optionalDropdownQueries: [{ query: "specialPurposeEntityId", fieldName: "specialPurposeEntityId" }]
      }
    ),
  ]);

  const handleSubmit = async (inputs, options) => {
    return options?.useFormInstance?.history?.push(`/loanapplications/addproperty?loanId=${inputs?.loanId}`);
  };

  const handleOnFormChanged = async (options, lastUpdated) => {
    if (lastUpdated === "loanId" && options?.inputs?.loanId) {
      const { data } = await fetchData(`/loans/${options?.inputs?.loanId}/details`);
      if (data?.specialPurposeEntityId) {
        options.updateInput("specialPurposeEntityId", data?.specialPurposeEntityId);
      }
    }
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      handleSubmit={handleSubmit}
      handleSubmitValidation={submitValidationIsLoanLost}
      handleClose={() => setIsModalOpen(false)}
      onFormChanged={handleOnFormChanged}
      actions={[
        "close",
        {
          action: "save",
          text: "Next",
          icon: GlobalConstants.Icons.CHEVRON_RIGHT,
        },
      ]}
    />
  );
};

const addLoanPropertyInlineTableEditingFields = (myForm) => {
  myForm.addFormInputField("Description", { inlineEdit: true });
  myForm.addDropdownField("Future Property Type", "metadata/list-property-types", { required: true, tableName: "futurePropertyType", tableValueName: "newStructurePropertyTypeId" });
  return myForm;
};

export const LmsLoanPropertyTable: FC<LmsLoanSharedTableByLoanIdProps> = ({ loanId, suffix = "", title, isServicing = false }) => {
  const addLoanModalAction = {
    action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
    text: "Add",
    icon: GlobalConstants.Icons.ADD,
    isDisabled: () => false,
    modalContent: (setIsModalOpen) => <SelectLoanForm setIsModalOpen={setIsModalOpen} key="select-loan-form" />
  };

  const addAction = loanId ? { action: "add", path: `/loanapplications/addproperty?loanId=${loanId}` } : addLoanModalAction;
  const editPath = loanId ? `loanapplications/editproperty/{id}?loanId=${loanId}` : (firstCheckedRow) => `loanapplications/editproperty/{id}?loanId=${firstCheckedRow?.loanId}`;
  const actionButtons = [
    addAction,
    { action: "copy", path: "/loanapplications/copyproperty", isDisabled: (checkedItems) => checkedItems?.length !== 1 },
    { action: "edit", path: editPath, isDisabled: (checkedItems) => checkedItems?.length !== 1 },
    "remove",
    "export",
    "save",
    "cancel",
    resendBudgetToInspectionCompanyBtn,
    {
      action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
      text: "Change Log",
      modalSize: "fullscreen",
      modalContent: (_, { firstCheckedRow }) => (
        <LmsTable
          isDisabled
          formatDataFunc={(data) => data.map((row, i) => ({ ...row, rowNo: i + 1 }))}
          apiSuffix={firstCheckedRow?.id ? `loanproperty/${firstCheckedRow?.id}/change-log-data` : ""}
          sortByArray={[
            "rowNo",
            "cooDateOld",
            "cooDateNew",
            "numberOfUnitsOld",
            "numberOfUnitsNew",
            "outOfPocketOld",
            "outOfPocketNew",
            "propertyTypeNew",
            "streetOld",
            "streetNew",
            "updatedOn",
            "updatedBy",
          ]}
          columnLabelsDictionary={{
            cooDateOld: "COO Date(Old)",
            cooDateNew: "COO Date(New)",
            outOfPocketOld: "Out Of Pocket(Old)",
            outOfPocketNew: "Out Of Pocket(New)",
            propertyTypeOld: "Property Type(Old)",
            propertyTypeNew: "Property Type(New)",
            streetOld: "Street(Old)",
            streetNew: "Street(New)",
            numberOfUnitsOld: "# Of Units(Old)",
            numberOfUnitsNew: "# Of Units(New)",
          }}
        />
      )
    },
    {
      text: "Export To Update",
      action: "downloadselected",
      downloadSuffix: "voting/loan-review/property-unit/export",
      downloadMany: true,
      method: "POST",
      icon: GlobalConstants.Icons.CARET_UP,
      payloadContentType: "JSON"
    } as any
  ];

  if (isServicing) {
    actionButtons.splice(1,1);
    actionButtons.splice(8,1);
  }

  return (
    <LmsTable
      actionButtons={actionButtons}
      apiSuffix={suffix || `loans/properties/${loanId}`}
      useApiFiltering={isServicing}
      deleteSuffix="loanproperty"
      onClickPath={loanId ? `/loanapplications/editproperty/{id}?loanId=${loanId}` : "loanapplications/editproperty"}
      title={title}
      saveSuffix="loanproperty"
      myForm={addLoanPropertyInlineTableEditingFields(new BCFormController())}
      hideProps={["newPropertyType", "needBorrowerApprovalBeforeSubmit"]}
      columnLabelsDictionary={TABLES.PROPERTIES}
    />
  );
};

export const loanDetailsEntitiesInlineTableEditingFields = (form) => {
  const LmsGuarantyOptions = [
    {
      key: "unconditional",
      text: "Unconditional",
      value: "Unconditional"
    },
    {
      key: "completionAndCarveout",
      text: "Completion and Carveout",
      value: "Completion and Carveout"
    }
  ];

  form.addDropdownField("Line Number", LmsGuarantyOptions, { inlineEdit: true });
  return form;
};

export const LmsLoanDetailsEntitiesByLoanIdTable: FC<LmsLoanSharedTableByLoanIdProps> = ({
   suffix = "",
   loanId,
   title,
   overrideDefaultTableProps
}) => {
  const defaultProps: LMSTableProps = {
    apiSuffix: suffix || `loans/entities/${loanId}`,
    actionButtons: [
      { action: "add", path: `loandetails/addentity?loanId=${loanId}` },
      "remove",
      "export",
      "save",
      "cancel",
      { text: "Edit Loan Officer", action: "add", path: `loandetails/editloanofficer?loanId=${loanId}`}
    ],
    columnClickOptions: {
      organization: { path: "organizations-borrowers", idProp: "organizationId" },
      organizationContact: { path: "organizations-contacts", idProp: "organizationContactId" }
    },
    title,
    deleteSuffix: "loans/entities"
  };
  return (
    <LmsTable
      {...{
        ...defaultProps,
        ...overrideDefaultTableProps
      }}
      columnLabelsDictionary={{
        applicationNumber: "Application #",
        loanNumber: "Loan #",
        organizationContact: "Name",
        detailEntered: "Detail Complete",
        borrowerOrganization: "Borrower",
        officePhone: "Business Phone"
      }}
      myForm={loanDetailsEntitiesInlineTableEditingFields(new BCFormController())}
      saveSuffix="loans/entities"
      // default took away guarantyOption
    />
  );
};

export const LmsLoanDetailsEntitiesTable: FC<LmsLoanSharedTableProps> = ({
  suffix,
  loanDetailsId,
  overrideDefaultTableProps
}) => {
  const defaultTableProps = {
    apiSuffix: suffix,
    deleteSuffix: "loans/entities",
    actionButtons: [
      { action: "add", path: `loandetails/addentity?loanId=${loanDetailsId}` },
      "remove",
      "export",
      "save",
      "cancel",
      { text: "Edit Loan Officer", action: "edit", path: `loandetails/editloanofficer/{id}?loanDetailsId=${loanDetailsId}&loanId=${loanDetailsId}` }
    ],
  };
  return (
    <LmsTable
      {...{
        ...defaultTableProps,
        ...overrideDefaultTableProps
      }}
    />
  );
};

export const LmsLoanApplicationsEntitiesTable = ({ loanId }) => {
  return (
    <LmsTable
      actionButtons={["export"]}
      apiSuffix={`loans/entities/${loanId}`}
      onClickPath="organizations-contacts"
      onClickIdProp="organizationContactId"
    />
  );
};

export const LmsLoanCommentsTable = ({ suffix = "", loanId, loanDetailsId = "", isReadOnly = false }) => {
  const editPath = appendParams("loandetails/editcomment/{id}", [{ loanDetailsId }, { loanId }, { isReadOnly }]);
  return <LmsTable
    apiSuffix={suffix ? suffix : loanId ? `loandetails/comments/${loanId}` : ""}
    onClickPath={editPath}
    actionButtons={isReadOnly ? [] : [
      { text: "Add", path: `/loandetails/addcomment?loanId=${loanId}` },
      { text: "Edit", path: editPath },
    ]}
    columnLabelsDictionary={{
      applicationNumber: "Application #",
      loanNumber: "Loan #",
      borrowerOrganization: "Borrower",
      name: "Project Name",
      description: "Comments"
    }}
  />;
};

export const LmsLoanConditionsTable = ({
  suffix,
  source,
  title = "",
  loanDetailsId,
  loanId,
  useApiFiltering = false,
  overrideDefaultTableProps,
}: IConditionsTableProps) => {
  const editPath = "servicing-loan-conditions";
  const defaultTableProps: LMSTableProps = {
    apiSuffix: suffix,
    title: title,
    deleteSuffix: "loandetails/conditions",
    onClickPath: editPath,
    myForm: loanConditionFields(new BCFormController()),
    saveSuffix: "loandetails/conditions",
    useApiFiltering,
    columnLabelsDictionary: {
      description: "Condition",
      conditionTiming: "Timing",
      conditionStatus: "Status",
      escrowInstruction: "Use as Escrow Instruction",
      loanNumber: "Loan #",
      applicationNumber: "Application #",
      borrowerOrganization: "Borrower",
      isAutoAddedYesNo: "Is Auto Added",
      brms: "BRMs",
      initialCos: "Initial COs",
      reviewCos: "Review Cos"
    },
    actionButtons: [
      { action: "add", path: appendParams("servicing-loan-conditions/add", [{ loanDetailsId }, { loanId }, { conditionSource: source }]) },
      { action: "edit", path: editPath },
      "remove",
      "export",
      "save",
      "cancel"
    ]
  };

  return (
    <LmsTable
      {...({
        ...defaultTableProps,
        ...overrideDefaultTableProps,
      })}
    />
  );
};

export const LmsLoanConditionsExportOnlyTable = ({ suffix, title = "" }) => {
  return (
    <LmsTable
      apiSuffix={suffix}
      actionButtons={["export"]}
      title={title}
      columnLabelsDictionary={TABLES.CONDITIONS}
      skipClick
    />
  );
};

export const LmsLoanBudgetDetailsModal = ({ clickedRowData, handleClose = undefined as any }) => {
  const [{ data, isLoading }] = useFetchData(`draws/draw-detail/${clickedRowData?.clickedRow?.id}`);
  const myForm = new BCFormController(data, { initIsDisabled: true });
  myForm.addCenteredGroup([
    myForm.getDisplayField("Borrower", { name: "borrowerOrganizationDisplay" }),
    myForm.getDisplayField("Loan Budget Detail", { name: "loanBudgetDetailDisplay" })
  ]);

  myForm.addCenteredGroup([
    myForm.getCurrencyField("Draw Requested Amount"),
    myForm.getCurrencyField("Draw Approved Amount")
  ]);

  const tabPages = {
    Vendors: <LmsTable 
      apiSuffix={`draws/detail-vendors/${clickedRowData?.clickedRow?.id}`} 
      actionButtons={["export"]} 
      onClickModalContent={(_, { clickedRowData }) => 
        <LmsTable 
          apiSuffix={`/orgs/bank-infolist/${clickedRowData?.clickedRow?.id}`} 
          deleteSuffix={"draws/rba"}
          actionButtons={[
            "export",
            { 
              text: "RBA",
              isDisabled: (checkedItems) => checkedItems?.length < 1,
              action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
              method: "PUT",
              submitSuffix: "draws/rba",
              refreshDataOnSuccess: true
            },
            { 
              text: "Remove RBA",
              isDisabled: (checkedItems) => checkedItems?.length < 1,
              action: GlobalConstants.TableDefaultActions.DELETE,
              refreshDataOnSuccess: true
            },
          ]} 
        />}
      onClickModalSize="fullscreen"
      columnClickOptions={{
        payeeOrganization: {
          idProp: "payeeOrganizationId",
          isModalContentLink: true,
        }
      }}
    />,
  };

  return <>
    <LmsFormGenerator 
      myForm={myForm} 
      isLoading={isLoading} 
      actions={["close"]} 
      handleClose={handleClose}
    />
    {data && <LmsTabPages tabPages={tabPages} />}
  </>;
};

export const LmsLoanDrawDetailsTable = ({ suffix }) => {
  return <LmsTable 
    apiSuffix={suffix} 
    actionButtons={["export"]} 
    columnLabelsDictionary={{
      payeeOrganizationCsv: "Payee Organization CSV",
      drawRequestedAmount: "Requested Amount",
      drawApprovedAmount: "Approved Amount",
      revisedAmount: "Draw Revised Amount"
    }}
    onClickModalContent={(setIsModalOpen, { clickedRowData }) => <LmsLoanBudgetDetailsModal clickedRowData={clickedRowData} handleClose={() => setIsModalOpen(false)} />}
    onClickModalSize="fullscreen"
    columnClickOptions={{
      loanBudgetDetail: {
        idProp: "id",
        isModalContentLink: true,
      }
    }}
  />;
};

export const LmsInspectionCompanyMessagesTable = ({ suffix, loanId = "", loanDrawId = "" }) => {
  const actions = [
    { action: "add", path: `servicing-inspection-company-messages/add${loanId && `/?loanId=${loanId}`}${loanDrawId && `&loanDrawId=${loanDrawId}`}`},
    { action: "edit", path: "servicing-inspection-company-messages" },
    "remove",
    "export",
    { text: "Respond", action: "edit", icon: GlobalConstants.Icons.CARET_UP, path: "/inspection-company-messages/respond" }
  ];

  return <LmsTable 
    actionButtons={actions} 
    apiSuffix={suffix} 
    columnClickOptions={{
      documentLog: { downloadPath: "document/download", idProp: "documentLogId" },
    }}
    columnLabelsDictionary={{
      nwccmessage: "Inspection Company Message",
      nwccfileType: "Inspection Company File Type"
    }}
  />;
};

export const LmsParametersTable = ({ id  }: IdProps) => {
  const actionButtons = [
    { text: "Add", icon: GlobalConstants.Icons.ADD, path: `/reports-lms-reports/addparameters?reportId=${id}` },
    { text: "Edit", icon: GlobalConstants.Icons.EDIT, path: `/reports-lms-reports/editparameters/{id}?reportId=${id}`, isDisabled: (checkedItems) => checkedItems?.length !== 1 },
    "remove",
    "export"
  ];

  return <LmsTable actionButtons={actionButtons} apiSuffix="" deleteSuffix="" onClickPath="" title="" />; 
};

export const LmsRolesTable = ({ id }: IdProps) => {
  const actionButtons = [
    { text: "Add", icon: GlobalConstants.Icons.ADD, path: `/reports-lms-reports/addroles?reportId=${id}` },
    "remove",
    "export"
  ];

  return <LmsTable actionButtons={actionButtons} apiSuffix="report/role-security" deleteSuffix="" onClickPath="" title="" />;
};

export const AdviceQuoteDataDisplayTable = ({ loanId }) => {
  return <LmsTable
    key={loanId}
    apiSuffix={`purchasequotes/advice-quote-data/${loanId}`} 
    isDisabled 
  />;
};

export const RevenueRecognitionDisplayTable = ({ mainFormOptions }) => {
  return <LmsTable
    apiSuffix={mainFormOptions?.inputs?.revenueRecognitionTempId ? `purchasequotes/revenue-recognition-temp-details/${mainFormOptions?.inputs?.revenueRecognitionTempId}` : ""}
    refreshDataDependency={mainFormOptions?.inputs?.revenueRecognitionTableData}
    isDisabled
    displayOnlyProps={[
      "name", 
      "recognizedDays", 
      "recognizedAmount", 
      "unrecognizedDays", 
      "unrecognizedAmount", 
      "allocationMethodOption", 
      "extraRevenueAmount", 
      "extraRevenuePercent"
    ]}
    columnLabelsDictionary={{ 
      allocationMethodOption: "Allocation Method", 
      extraRevenuePercent: "Extra Revenue %"
    }}
    compact
  />;
};

export const PurchaseQuoteLoanTransactionTable = ({ mainFormOptions }) => {
  return <LmsTable 
    apiSuffix={mainFormOptions?.inputs?.purchaseQuoteLoanTransactionTempId ? `purchasequotes/loan-transaction-temp-data/${mainFormOptions?.inputs?.purchaseQuoteLoanTransactionTempId}` : ""}
    refreshDataDependency={mainFormOptions?.inputs?.refreshLoanTransactionTable}
    skipCache={true}
    isDisabled
    displayOnlyProps={["transEffectiveDate", "category", "type", "transactionAmount", "customAmount", "apiece", "bpiece", "notional"]}
    columnLabelsDictionary={{ 
      transEffectiveDate: "Tr Eff Date", 
      transactionAmount: "Tr Amount", 
      apiece: "A Piece", 
      bpiece: "B Piece" 
    }}
    compact
  />;
};

export const PayoffQuoteUnitInlineTableEdit = ({ options }) => {
  const UnitModalFormFields = (myForm) => {
    myForm.addCenteredGroup([
      myForm.getCurrencyField("actualSalesPrice", { inlineEdit: true })
    ]);

    return myForm;
  };

  const onSaveSuccessFunction = (updatedFields, options) => {
    if (options) {
      options.setInputs({ ...options.inputs, lastUpdatedUnitId: updatedFields?.id || guid() });
    }
  };

  return <LmsTable
    apiSuffix={options?.inputs?.loanId ? `/payoffquotes/property-unit-reconveyance/${options?.inputs?.loanId}` : ""}
    actionButtons={["export", "save", "cancel"]}
    columnLabelsDictionary={{
      unitNumber: "Unit #",
      loanNumber: "Loan #",
      applicationNumber: "Application #",
      appraisedValue: "Underwritten Value",
      estimatedSalesPrice: "As Repaired Value",
      numberOfBathroom: "Number of Bathrooms",
      numberOfBedroom: "Number of Bedrooms",
      borrowerOrganization: "Borrower",
      brms: "BRMs",
      unitReconveyanceReference: "Reconveyance Number",
      reviewCos: "Review COs",
      initialCos: "Initial COs",
      timeFrameToSellOrRefinanceInMonths: "Sell Refi Timeframe",
      buildTimeInMonths: "Work Duration",
      partialDeedReleased: "Paid Off",
      unitReconveyanceComments: "Reconveyance Comments",
      unitReconveyanceDate: "Reconveyance Date"
    }}
    skipClick
    myForm={UnitModalFormFields(new BCFormController())}
    saveSuffix="loanproperty/units"
    onSaveSuccessFunction={(updatedFields) => onSaveSuccessFunction(updatedFields, options)}
  />;
};
